import { useContext } from 'react'
import { Helmet } from 'react-helmet-async'
import { useLocation } from 'react-router-dom'

import { faBan, faCheckCircle } from '@fortawesome/pro-light-svg-icons'

import {
  Box, Button, Columns, DashboardModule, Grid, LoadingModule, PageHeader,
} from '@domain-group/suit-theme'

import { getQueryParamsFromHash, parsePermittedQueryParams } from '@domain-group/javascript-utils'

import PageContext from '@contexts/pageContext'
import type { DefaultContext } from '@contexts/pageContext'

import useTemplatePages from '@hooks/useTemplatePages'
import { generateUrls } from '@hooks/useTemplatePage'

import type { TemplatePageModel } from '@models/types'

import ListBlankState from '@components/ListBlankState'
import PageFilterModule from '@components/PageFilterModule'
import TemplatePageGridTile from '@components/TemplatePageGridTile'

import NoFiltersBlankState from '../NoFiltersBlankState'

import type { PageContextPayload } from '../../index'

const buildPageFilterFields = () => {
  const style = { marginBottom: 16 }
  const boxProps = { marginBottom: 'large' }

  return [
    {
      component: 'DynamicInput',
      componentProps: {
        inputComponent: 'input',
        style,
      },
      key: 'string',
      label: 'Filter by Title or ID',
    },
    {
      component: 'EntitySelector',
      componentProps: {
        boxProps,
        entityKey: 'brands',
        label: 'Brand',
      },
      key: 'brand_id',
    },
    {
      callbacks: {
        // @ts-expect-error TS7031
        buildSearchRequestOptions: ({ brand_id }) => ({ brand_id }),
      },
      component: 'EntitySelector',
      componentProps: {
        boxProps,
        entityKey: 'templateGroups',
        label: 'Template Group',
      },
      key: 'template_group_id',
    },
    {
      component: 'EntitySelector',
      componentProps: {
        boxProps,
        entityKey: 'brandThemes',
        label: 'Brand Theme',
      },
      key: 'brand_theme_id',
    },
    {
      callbacks: {
        // @ts-expect-error TS7031
        buildSearchRequestOptions: ({ brand_id }) => ({ brand_id }),
      },
      component: 'EntitySelector',
      componentProps: {
        boxProps,
        entityKey: 'agencies',
        label: 'Agency',
      },
      key: 'agency_id',
    },
    {
      component: 'EntitySelector',
      componentProps: {
        boxProps,
        entityKey: 'services',
        label: 'Service',
      },
      key: 'service_id',
    },
    {
      callbacks: {
        // @ts-expect-error TS7031
        buildSearchRequestOptions: ({ service_id }) => ({ service_id }),
      },
      component: 'EntitySelector',
      componentProps: {
        boxProps,
        entityKey: 'serviceProducts',
        label: 'Product',
      },
      key: 'service_product_id',
    },
    {
      component: 'EntitySelector',
      componentProps: {
        boxProps,
        entityKey: 'providers',
        label: 'Provider',
      },
      key: 'provider_id',
    },
    {
      component: 'EntitySelector',
      componentProps: {
        boxProps,
        entityKey: 'publications',
        label: 'Publication',
      },
      key: 'publication_id',
    },
  ]
}

interface PageContentProps {
  pageFilters: {
    [key: string]: any,
  },
}

const PageContent = (props: PageContentProps) => {
  const { pageFilters } = props

  // useLocation to refresh the window.location. If not used location won't reload if URL changes
  useLocation()

  const hasSelectedFilters = Object.keys(pageFilters).some(key => key !== 'hidden')

  const { hidden } = parsePermittedQueryParams(getQueryParamsFromHash(), ['hidden'])
  const isHidden = hidden ?? 'false'

  const templatePagesPayload = useTemplatePages({
    filters: {
      ...pageFilters,
      hidden: isHidden === 'true',
    },
    performHttpRequests: !!hasSelectedFilters,
    requestOptions: {
      include_template_page_dimensions: true,
      include_template_page_permission_ids: true,
      include_template_page_preview: true,
    },
  })

  const {
    callbacks: {
      loadMore,
    },
    canLoadMore,
    filteredTemplatePages,
    filteredTemplatePagesCount,
    hasFilteredTemplatePages,
    loading,
  } = templatePagesPayload

  // @ts-expect-error TS2345
  const pageContext = useContext<DefaultContext<PageContextPayload>>(PageContext)
  const {
    callbacks: {
      resetFilters,
      updateFilters,
    },
    defaultFilters,
  } = pageContext

  const { templatePagesIndexUrl, archivedTemplatePagesIndexUrl } = generateUrls()

  return (
    <>
      <Helmet>
        <title>Templates | Template Manager</title>
      </Helmet>

      <PageHeader
        // @ts-expect-error TS2322
        activeTabBarItemKey={isHidden}
        boxProps={{ height: [112, 105], justifyContent: 'flex-start' }}
        tabBarItems={[
          {
            href: templatePagesIndexUrl,
            icon: faCheckCircle,
            key: 'false',
            title: 'Current',
          },
          {
            href: archivedTemplatePagesIndexUrl,
            icon: faBan,
            key: 'true',
            title: 'Archived',
          },
        ]}
        title="Templates"
      />

      <Box paddingX="large" paddingTop={[112, 105]}>
        <Columns boxProps={{ marginTop: 'large' }} flexDirection={['column', 'column', 'row']}>
          <Columns.Main>
            <LoadingModule loading={loading} times={3} />

            {!loading && !hasSelectedFilters && <NoFiltersBlankState />}

            {!loading && !hasFilteredTemplatePages && hasSelectedFilters && <ListBlankState />}

            {!loading && hasFilteredTemplatePages && (
              <DashboardModule contentBoxProps={{ flexDirection: 'column' }} title="Template Pages">
                <Box flexDirection="column">
                  <Grid
                    // @ts-expect-error TS2322
                    gridGap="medium"
                    gridAutoRows="1fr"
                    gridTemplateColumns="repeat(auto-fill, minmax(240px, 1fr))"
                    gridTemplateRows="repeat(auto-fill, minmax(240px, 1fr))"
                    padding="large"
                  >
                    {filteredTemplatePages.map((templatePage: TemplatePageModel) => (
                      <TemplatePageGridTile
                        // @ts-expect-error TS2741
                        callbacks={{}}
                        key={templatePage.id}
                        templatePage={templatePage}
                      />
                    ))}
                  </Grid>
                </Box>

                <DashboardModule.LoadMoreFooter
                  callbacks={{ loadMore }}
                  canLoadMore={canLoadMore}
                  entityCount={filteredTemplatePagesCount}
                  loading={loading}
                />
              </DashboardModule>
            )}
          </Columns.Main>

          <Columns.Sidebar>
            <PageFilterModule
              callbacks={{ resetFilters, updateFilters }}
              defaultFilters={defaultFilters}
              // @ts-expect-error TS2322
              filterFields={buildPageFilterFields()}
              pageFilters={pageFilters}
            />
          </Columns.Sidebar>
        </Columns>
      </Box>
    </>
  )
}

export default PageContent
